import { ref } from '@vue/composition-api';
import { IAdCampaignItem } from '@/bundles/Ad/interfaces';
import { IGetCampaignItemsDto, IUnassignAdItemDto, IUpdateAdItemInfoDto } from '@/bundles/Ad/dto';
import { adCampaignItemService } from '@/bundles/Ad/factory/adCampaignItemServiceFactory';
import AdCampaign from '@/bundles/Ad/models/AdCampaign';
import { useAdCampaign } from '@/bundles/Ad/composables/useAdCampaign';
import AdCampaignItem from '@/bundles/Ad/models/AdCampaignItem';
import { AdCampaignItemMediaType } from '@/bundles/Ad/types';

export function useAdCampaignItem () {
  const { defaultCampaignData } = useAdCampaign();
  const totals = {
      impressions: 0,
      clicks: 0
  };
  function mapTypeTitle(key: string) {
    switch (key) {
      case 'video':
        return 'Video';
      case 'carousel':
        return 'Carousel';
      default:
        return 'Image';
    }
  }
  const mediaTypes = Object.keys(AdCampaignItemMediaType).map((item) => ({ title: mapTypeTitle(item), value: AdCampaignItemMediaType[item as AdCampaignItemMediaType]}));

  const defaultItemData: IAdCampaignItem = {
    impressions: 0,
    clicks: 0,
    image: '',
    last_shown_at: new Date(),
    created: new Date(),
    updated: new Date(),
    id: 0,
    campaign: new AdCampaign(defaultCampaignData),
    url: '',
    media_type: AdCampaignItemMediaType.image,
    media: []
  };

  const adCampaignItems = ref<IAdCampaignItem[]>([]);
  const listLoading = ref(false);
  const total = ref(0);
  async function fetch (params: IGetCampaignItemsDto) {
    listLoading.value = true;
    const response = await adCampaignItemService.getItems(params);
    adCampaignItems.value = response.data;
    total.value = response.total;
    totals.clicks = 0;
    totals.impressions = 0;

    for (const item of adCampaignItems.value) {
      totals.impressions += Number(item.impressions);
      totals.clicks += Number(item.clicks);
    }
    listLoading.value = false;
  }

  const adCampaignItem = ref<IAdCampaignItem>(new AdCampaignItem(defaultItemData));
  const editLoading = ref(false);
  async function update (id: number, payload: IUpdateAdItemInfoDto) {
    editLoading.value = true;
    adCampaignItem.value = await adCampaignItemService.update(id, payload);
    editLoading.value = false;
  }
  function resetAdItem () {
    adCampaignItem.value = new AdCampaignItem(defaultItemData);
  }

  const downloadLoading = ref(false);
  async function exportToExcel(params: IGetCampaignItemsDto) {
    downloadLoading.value = true;
    const link = await adCampaignItemService.exportItems(params);
    window.location.replace(link);
    downloadLoading.value = false;
  }

  const createLoading = ref(false);
  const unassignLoading = ref(false);
  async function assignAdItem (id: number, payload: IUpdateAdItemInfoDto) {
    createLoading.value = true;
    await adCampaignItemService.assignAdItem(id, payload)
      .finally(() => createLoading.value = false);
  }
  async function unassignAdItem (id: number, payload: IUnassignAdItemDto) {
    unassignLoading.value = true;
    await adCampaignItemService.unassignAdItems(id, payload)
      .finally(() => unassignLoading.value = false);
  }

  const bulkUpdateOrderLoading = ref(false);
  async function bulkUpdateOrder (payload: { items: Array<{ id: number; order: number }> }) {
    bulkUpdateOrderLoading.value = true;
    await adCampaignItemService.bulkUpdateOrder(payload)
      .finally(() => bulkUpdateOrderLoading.value = false);
  }

  return {
    adCampaignItems,
    listLoading,
    total,
    totals,
    adCampaignItem,
    editLoading,
    mediaTypes,
    downloadLoading,
    defaultItemData,
    createLoading,
    unassignLoading,
    bulkUpdateOrderLoading,
    unassignAdItem,
    assignAdItem,
    bulkUpdateOrder,
    fetch,
    resetAdItem,
    update,
    exportToExcel,
  };
}
